import {
  LocationMarkerIcon,
  PaperClipIcon,
  PencilIcon,
} from "@heroicons/react/outline";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { styled } from "~/config/stitches";
import { Badge } from "~/design-system/Badge";
import { Button } from "~/design-system/Button";
import { ImagePreview } from "~/design-system/ImagePreview";
import { fileIsAnImage } from "~/screens/App/screens/Project/Tasks/utils";
import { useFileBadge_DataQuery } from "./FileBadge.graphql";

type FileBadgeProps = {
  fileVersionId: string;
  onRemove: (fileVersionId: string) => void;
  onEditLocation?: (fileVersionId: string) => void;
  location?: {
    page_number: number;
    x: number;
    y: number;
  } | null;
};

// Define interface for location type
interface FileLocation {
  page_number: number;
  x: number;
  y: number;
}

const BadgeContainer = styled("div", {
  alignItems: "center",
  display: "flex",
  gap: "$2",
});

const useFileBadge = (props: FileBadgeProps) => {
  const [{ data }] = useFileBadge_DataQuery({
    variables: { fileVersionId: props.fileVersionId },
  });

  const handleRemove: React.MouseEventHandler = () => {
    props.onRemove(props.fileVersionId);
  };

  const handleEditLocation = () => {
    if (props.onEditLocation) {
      props.onEditLocation(props.fileVersionId);
    }
  };

  const graphqlLocation = (
    data?.file_versions_by_pk?.tasks as
      | { location?: FileLocation }[]
      | undefined
  )?.[0]?.location;

  const fileLocation = props.location ?? graphqlLocation;
  const hasLocation = Boolean(fileLocation);

  const isImage = data?.file_versions_by_pk
    ? fileIsAnImage(data?.file_versions_by_pk?.content_type)
    : undefined;

  return {
    actions: {
      handleEditLocation,
      handleRemove,
    },
    state: {
      fileLocation,
      fileVersion: data?.file_versions_by_pk,
      hasLocation,
      isImage,
    },
  };
};

function FileBadge(props: FileBadgeProps) {
  const { actions, state } = useFileBadge(props);
  const { t } = useTranslation();
  if (!state.fileVersion) {
    return null;
  }

  if (state.isImage) {
    return (
      <ImagePreview
        size="medium"
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        src={state.fileVersion.url!}
        onRemoveClick={actions.handleRemove}
      />
    );
  }

  if (state.hasLocation) {
    return (
      <BadgeContainer>
        <Badge
          size="large"
          shape="rounded"
          onRemove={actions.handleRemove}
          leadingIcon={<LocationMarkerIcon />}
        >
          {state.fileVersion.name}
        </Badge>

        <Button
          leadingIcon={<PencilIcon />}
          dataClovisActionId="edit-location"
          size="xsmall"
          variant="ghost"
          type="button"
          onClick={actions.handleEditLocation}
          css={{
            height: "$7",
            minWidth: "auto",
          }}
        >
          {t(
            "screens.Project.CreateTaskForm.TaskFilesBadges.FileBadge.editLocation",
            "Edit location"
          )}
        </Button>
      </BadgeContainer>
    );
  }

  return (
    <Badge
      size="large"
      shape="rounded"
      onRemove={actions.handleRemove}
      leadingIcon={<PaperClipIcon />}
    >
      {state.fileVersion.name}
    </Badge>
  );
}

export type { FileBadgeProps };
export { FileBadge };
